/**
 * Dual setup checklists for Planning Engine overview surfaces (desktop + mobile).
 *
 * Viability = rough-cut capacity (can we afford the set?).
 * Launch readiness = time-phased execution (can we place launches?).
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Circle, XCircle } from 'lucide-react';
import { getCapacityRows, getPriorityCapacityRows, getPriorities, getTeamParticipation } from '../../../lib/planningEngineService';
import { PLANNING_MODE_LABELS } from '../../../lib/planningEngineModule.types';
import type { Scenario } from '../../../lib/planningEngineModule.types';
import type { PlanningEngineOutput } from '../../../lib/planningEngineModule.types';
import { getLaunchProfiles, getSupplyCalendar } from '../../../lib/timePhasedPlanningService';
import { getFundingEnvelopes } from '../../../lib/timePhasedFinancialService';

export type CheckStatus = 'ok' | 'warn' | 'missing' | 'pending';

export function CheckRow({
  status,
  label,
  detail,
  action,
}: {
  status: CheckStatus;
  label: string;
  detail?: string;
  action?: { label: string; href?: string; onClick?: () => void };
}) {
  const icon =
    status === 'ok' ? (
      <CheckCircle2 className="h-4 w-4 text-[var(--green)] dark:text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
    ) : status === 'warn' ? (
      <AlertTriangle className="h-4 w-4 text-[var(--mustard)] dark:text-[var(--mustard)] flex-shrink-0 mt-0.5" />
    ) : status === 'missing' ? (
      <XCircle className="h-4 w-4 text-[var(--coral)] dark:text-[var(--coral)] flex-shrink-0 mt-0.5" />
    ) : (
      <Circle className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
    );

  return (
    <div className="flex items-start gap-3 py-3 border-b border-[var(--rule)] dark:border-[var(--rule)] last:border-0">
      {icon}
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm font-medium ${
            status === 'missing'
              ? 'text-[var(--coral)] dark:text-[var(--coral)]'
              : status === 'warn'
                ? 'text-[var(--mustard)] dark:text-[var(--mustard)]'
                : 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'
          }`}
        >
          {label}
        </p>
        {detail && (
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5">{detail}</p>
        )}
      </div>
      {action &&
        (action.onClick ? (
          <button
            type="button"
            onClick={action.onClick}
            className="shrink-0 text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:underline"
          >
            {action.label} →
          </button>
        ) : action.href ? (
          <Link
            to={action.href}
            className="shrink-0 text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:underline"
          >
            {action.label} →
          </Link>
        ) : null)}
    </div>
  );
}

type LaunchReadinessState = {
  supplyRows: number | null;
  profileCount: number | null;
  prioritiesTotal: number | null;
  prioritiesWithProfile: number | null;
  participationCount: number | null;
  fundingEnvelopeCount: number | null;
};

export function PlanningEngineSetupChecklist({
  activeScenario,
  projectId,
  planOutput,
  executiveSummary,
  onCreateScenario,
  basePath = '/planning/engine',
  timePhasedEnabled = false,
}: {
  activeScenario: Scenario | null;
  projectId: string | null;
  planOutput: PlanningEngineOutput | null;
  executiveSummary: PlanningEngineOutput['executiveSummary'] | null;
  onCreateScenario: () => void;
  /** Route prefix for checklist action links (desktop vs mobile). */
  basePath?: string;
  /** When true, show Launch readiness checklist (time-phased execution). */
  timePhasedEnabled?: boolean;
}) {
  const [capacityCount, setCapacityCount] = useState<number | null>(null);
  const scenarioMode = (activeScenario as { planningMode?: string } | null)?.planningMode;
  const [launch, setLaunch] = useState<LaunchReadinessState>({
    supplyRows: null,
    profileCount: null,
    prioritiesTotal: null,
    prioritiesWithProfile: null,
    participationCount: null,
    fundingEnvelopeCount: null,
  });

  useEffect(() => {
    if (!projectId || !activeScenario?.id) {
      setCapacityCount(null);
      return;
    }
    // Capacity lives in a different table per planning mode: priority_estimate
    // writes planning_priority_capacity, everything else writes planning_capacity.
    // This row only ever counted the latter, so a priority_estimate scenario with
    // capacity configured still reported "No capacity rows yet" — the checklist
    // told you to do the thing you had just done, and the Run gate stayed shut.
    const read = scenarioMode === 'priority_estimate' ? getPriorityCapacityRows : getCapacityRows;
    void read(projectId, activeScenario.id).then((rows) => setCapacityCount(rows.length));
  }, [projectId, activeScenario, scenarioMode]);

  useEffect(() => {
    if (!timePhasedEnabled || !projectId || !activeScenario?.id) {
      setLaunch({
        supplyRows: null,
        profileCount: null,
        prioritiesTotal: null,
        prioritiesWithProfile: null,
        participationCount: null,
        fundingEnvelopeCount: null,
      });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const [supply, profiles, priorities, participation, envelopes] = await Promise.all([
          getSupplyCalendar(activeScenario.id),
          getLaunchProfiles(projectId),
          getPriorities(projectId),
          getTeamParticipation(projectId),
          getFundingEnvelopes(activeScenario.id),
        ]);
        if (cancelled) return;
        // Prefer scenario-owned priorities so scoped pools aren't drowned by the whole project.
        const scoped = priorities.filter((p) => p.scenarioId === activeScenario.id);
        const inScope = scoped.length > 0 ? scoped : priorities;
        const withProfile = inScope.filter((p) => p.launchProfileId).length;
        setLaunch({
          supplyRows: supply.length,
          profileCount: profiles.length,
          prioritiesTotal: inScope.length,
          prioritiesWithProfile: withProfile,
          participationCount: participation.length,
          fundingEnvelopeCount: envelopes.length,
        });
      } catch {
        if (!cancelled) {
          setLaunch({
            supplyRows: 0,
            profileCount: 0,
            prioritiesTotal: 0,
            prioritiesWithProfile: 0,
            participationCount: 0,
            fundingEnvelopeCount: 0,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [timePhasedEnabled, projectId, activeScenario?.id]);

  const noCapacityModelCount = executiveSummary?.noCapacityModelCount ?? 0;
  const needsSizingCount = executiveSummary?.needsSizingCount ?? 0;
  const totalItems = executiveSummary?.totalWorkUnits ?? 0;
  const hasRun = planOutput != null;

  const profileCoverageWarn =
    launch.prioritiesTotal != null &&
    launch.prioritiesTotal > 0 &&
    launch.prioritiesWithProfile != null &&
    launch.prioritiesWithProfile < launch.prioritiesTotal;

  return (
    <div className="space-y-4">
      {/* ── Viability ───────────────────────────────────────────────────── */}
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] p-4 md:p-6">
        {/* Before the first run this list is the point of the page — the engine
            already knows exactly what is missing, so it reads as prerequisites
            rather than as a post-mortem on a run that disappointed. */}
        <h2 className="text-sm font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider mb-1">
          {hasRun ? 'Viability setup' : 'What you need set up in order to run'}
        </h2>
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-4">
          {hasRun
            ? 'Rough-cut capacity — confirm the candidate set is realistic before detailed planning.'
            : 'Rough-cut capacity. Every row links to where it is set; the engine can answer once they are green.'}
        </p>

        <CheckRow
          status={activeScenario ? 'ok' : 'missing'}
          label="Scenario created and active"
          detail={
            activeScenario
              ? `"${activeScenario.name}" · ${PLANNING_MODE_LABELS[activeScenario.planningMode]} mode · ${activeScenario.planningWeeks} weeks`
              : 'Create a scenario to define planning parameters (horizon, mode, buffer).'
          }
          action={activeScenario ? undefined : { label: 'Create scenario', onClick: onCreateScenario }}
        />

        <CheckRow
          status={
            !activeScenario
              ? 'pending'
              : capacityCount == null
                ? 'pending'
                : capacityCount === 0
                  ? 'missing'
                  : 'ok'
          }
          label="Resource budget (WU capacity) defined"
          detail={
            !activeScenario
              ? 'Requires a scenario first.'
              : capacityCount == null
                ? 'Checking…'
                : capacityCount === 0
                  ? 'No capacity rows yet. Add at least one resource type with available work units.'
                  : `${capacityCount} row${capacityCount !== 1 ? 's' : ''} configured for this scenario.`
          }
          action={
            activeScenario ? { label: 'Resource budget', href: `${basePath}/capacity` } : undefined
          }
        />

        <CheckRow
          status={
            !activeScenario
              ? 'pending'
              : !hasRun
                ? 'pending'
                : totalItems === 0
                  ? 'missing'
                  : needsSizingCount === 0
                    ? 'ok'
                    : needsSizingCount < totalItems
                      ? 'warn'
                      : 'missing'
          }
          label="Items have demand estimates"
          detail={
            !activeScenario
              ? 'Requires a scenario first.'
              : !hasRun
                ? 'Run viability to check sizing status.'
                : totalItems === 0
                  ? 'Engine instance has 0 priorities. Sync or seed items, then re-run.'
                  : needsSizingCount === 0
                    ? `All ${totalItems} item${totalItems !== 1 ? 's' : ''} have a demand estimate.`
                    : `${needsSizingCount} item${needsSizingCount !== 1 ? 's' : ''} need sizing.`
          }
          action={
            totalItems === 0 || needsSizingCount > 0
              ? { label: 'Open Priorities', href: `${basePath}/priorities` }
              : undefined
          }
        />

        <CheckRow
          status={!hasRun ? 'pending' : noCapacityModelCount === 0 ? 'ok' : 'warn'}
          label="All items matched to capacity"
          detail={
            !hasRun
              ? 'Run viability to check.'
              : noCapacityModelCount === 0
                ? 'Every item matched a capacity pool.'
                : `${noCapacityModelCount} item${noCapacityModelCount !== 1 ? 's' : ''} have a resource type with no matching capacity row.`
          }
          action={
            noCapacityModelCount > 0
              ? { label: 'Resource budget', href: `${basePath}/capacity` }
              : undefined
          }
        />
      </div>

      {/* ── Launch readiness ────────────────────────────────────────────── */}
      {timePhasedEnabled && (
        <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--slate)] dark:border-[var(--slate)]/40 p-4 md:p-6">
          <h2 className="text-sm font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider mb-1">
            Launch planning setup
          </h2>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-4">
            Time-phased execution — shared-team supply, workload templates, participation, and funding.
            Separate from viability WU capacity.
          </p>

          <CheckRow
            status={
              !activeScenario
                ? 'pending'
                : launch.supplyRows == null
                  ? 'pending'
                  : launch.supplyRows === 0
                    ? 'missing'
                    : 'ok'
            }
            label="Shared-team supply calendar"
            detail={
              !activeScenario
                ? 'Requires a scenario first.'
                : launch.supplyRows == null
                  ? 'Checking…'
                  : launch.supplyRows === 0
                    ? 'No supply rows yet. This dial drives Launch Calendar placement — not Resource Budget.'
                    : `${launch.supplyRows} supply row${launch.supplyRows !== 1 ? 's' : ''} on the calendar.`
            }
            action={
              activeScenario
                ? { label: 'Supply calendar', href: `${basePath}/time-phased?tab=supply` }
                : undefined
            }
          />

          <CheckRow
            status={
              !activeScenario
                ? 'pending'
                : launch.profileCount == null
                  ? 'pending'
                  : launch.profileCount === 0
                    ? 'missing'
                    : profileCoverageWarn
                      ? 'warn'
                      : 'ok'
            }
            label="Workload templates on priorities"
            detail={
              !activeScenario
                ? 'Requires a scenario first.'
                : launch.profileCount == null
                  ? 'Checking…'
                  : launch.profileCount === 0
                    ? 'No workload templates in the project. Seed or create profiles, then attach to priorities.'
                    : launch.prioritiesTotal === 0
                      ? `${launch.profileCount} profile${launch.profileCount !== 1 ? 's' : ''} available — add priorities to attach.`
                      : launch.prioritiesWithProfile === launch.prioritiesTotal
                        ? `All ${launch.prioritiesTotal} priorit${launch.prioritiesTotal !== 1 ? 'ies' : 'y'} have a workload template.`
                        : `${launch.prioritiesWithProfile ?? 0} of ${launch.prioritiesTotal} priorities have a workload template.`
            }
            action={{ label: 'Launch setup', href: `${basePath}/time-phased?tab=setup` }}
          />

          <CheckRow
            status={
              !activeScenario
                ? 'pending'
                : launch.participationCount == null
                  ? 'pending'
                  : launch.participationCount === 0
                    ? 'warn'
                    : 'ok'
            }
            label="Shared-team participation"
            detail={
              !activeScenario
                ? 'Requires a scenario first.'
                : launch.participationCount == null
                  ? 'Checking…'
                  : launch.participationCount === 0
                    ? 'No participation rows yet. Optional for a first run, required for buildable ≠ launchable honesty.'
                    : `${launch.participationCount} participation requirement${launch.participationCount !== 1 ? 's' : ''}.`
            }
            action={{ label: 'Participation', href: `${basePath}/participation` }}
          />

          <CheckRow
            status={
              !activeScenario
                ? 'pending'
                : launch.fundingEnvelopeCount == null
                  ? 'pending'
                  : launch.fundingEnvelopeCount === 0
                    ? 'warn'
                    : 'ok'
            }
            label="Funding envelope & launch costs"
            detail={
              !activeScenario
                ? 'Requires a scenario first.'
                : launch.fundingEnvelopeCount == null
                  ? 'Checking…'
                  : launch.fundingEnvelopeCount === 0
                    ? 'No funding envelope yet. Money is the FIN peer of supply hours — set when the story needs dollars.'
                    : `${launch.fundingEnvelopeCount} funding envelope${launch.fundingEnvelopeCount !== 1 ? 's' : ''} configured.`
            }
            action={{ label: 'Funding & costs', href: `${basePath}/funding` }}
          />
        </div>
      )}
    </div>
  );
}
